import React from 'react';
import { XMarkIcon, MapPinIcon, ClockIcon, CalendarIcon, UserIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { CalendarEvent } from '../../api/calendar';

interface EventDetailProps {
  event: CalendarEvent;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export default function EventDetail({ event, onClose, onEdit, onDelete }: EventDetailProps) {
  const startDate = new Date(event.start_datetime);
  const endDate = new Date(event.end_datetime);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface-900 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-700">
          <h2 className="text-2xl font-bold text-white">{event.name}</h2>
          <button
            onClick={onClose}
            className="text-surface-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Color indicator */}
          <div className="flex items-center gap-3">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: event.color }}
            />
            <span className="text-surface-400 text-sm">Event Color</span>
          </div>

          {/* Date and Time */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CalendarIcon className="w-5 h-5 text-primary-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-surface-400">Date</p>
                <p className="text-white font-medium">
                  {format(startDate, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <ClockIcon className="w-5 h-5 text-primary-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-surface-400">Time</p>
                <p className="text-white font-medium">
                  {event.all_day ? (
                    'All Day'
                  ) : (
                    <>
                      {format(startDate, 'h:mm a')} - {format(endDate, 'h:mm a')}
                    </>
                  )}
                </p>
              </div>
            </div>

            {event.location && (
              <div className="flex items-start gap-3">
                <MapPinIcon className="w-5 h-5 text-primary-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-surface-400">Location</p>
                  <p className="text-white font-medium">{event.location}</p>
                </div>
              </div>
            )}
          </div>

          {/* Assigned Members */}
          {event.members && event.members.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <UserIcon className="w-5 h-5 text-primary-400" />
                <p className="text-sm text-surface-400">Assigned Members</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {event.members.map((member) => {
                  const displayName = member.nickname || member.first_name || member.username || 'Unknown';
                  const avatarColor = member.color || '#6366f1';
                  
                  return (
                    <div
                      key={member.id}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white border-2 border-surface-700 hover:border-primary-400 transition-colors"
                      style={{ backgroundColor: avatarColor }}
                      title={displayName + (member.last_name ? ` ${member.last_name}` : '')}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div>
              <p className="text-sm text-surface-400 mb-2">Description</p>
              <p className="text-white whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t border-surface-700">
            <p className="text-xs text-surface-500">
              Created {format(new Date(event.created_at), 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        {/* Actions */}
        {(onEdit || onDelete) && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-700">
            {onDelete && (
              <button
                onClick={onDelete}
                className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
              >
                Delete
              </button>
            )}
            {onEdit && (
              <button
                onClick={onEdit}
                className="btn-primary"
              >
                Edit
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

